import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, LogOut, Timer, BookOpen, PlayCircle, ChevronRight, Lock } from "lucide-react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ReviewWidget } from "../components/ReviewWidget";

const MODE_META = {
  testing: { label: "Testing Mode", icon: Timer, hint: "230 min · timed · Pearson VUE style" },
  study: { label: "Study Mode", icon: BookOpen, hint: "Untimed · check answers as you go" },
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [exams, setExams] = useState(null);
  const [accessEnded, setAccessEnded] = useState(false);
  const [starting, setStarting] = useState(null);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    api.get("/exams")
      .then(({ data }) => setExams(data.exams))
      .catch((e) => {
        if (e.response?.status === 403) setAccessEnded(true);
        else toast.error(apiError(e, "Could not load exams"));
      });
  }, []);

  const start = (examNumber, mode) => setPending({ examNumber, mode });

  const beginExam = async () => {
    const { examNumber, mode } = pending;
    setPending(null);
    setStarting(`${examNumber}-${mode}`);
    try {
      const { data } = await api.post("/exam-sessions", { exam_number: examNumber, mode });
      if (data.resumed) toast.info("Resuming your in-progress attempt");
      navigate(`/exam/${data.id}`);
    } catch (e) {
      toast.error(apiError(e, "Could not start exam"));
    } finally {
      setStarting(null);
    }
  };

  const inProgress = (exams || []).flatMap((ex) =>
    Object.values(ex.in_progress || {}).map((a) => ({ ...a, exam_number: ex.exam_number }))
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="leading-tight">
            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-blue-800">Instructing.com</div>
            <div className="font-semibold text-slate-900 text-sm sm:text-base">PMP® Practice Exams</div>
          </div>
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <button data-testid="admin-link" onClick={() => navigate("/admin")}
                className="text-sm font-medium text-blue-700 hover:text-blue-900 px-3 py-1.5 rounded-md hover:bg-blue-50 transition-colors">
                Admin
              </button>
            )}
            <span data-testid="user-email" className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={async () => { await logout(); navigate("/login"); }}
              className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        {accessEnded ? (
          <div data-testid="access-ended-banner" className="bg-white border border-amber-200 rounded-xl p-8 max-w-xl">
            <Lock className="w-8 h-8 text-amber-500 mb-4" strokeWidth={1.5} />
            <h2 className="text-xl font-semibold text-slate-900">Your access period has ended</h2>
            <p className="mt-2 text-slate-500 text-sm leading-relaxed">
              Your 2-year practice exam access has expired. Contact{" "}
              <a href="mailto:support@instructing.com" className="underline">support@instructing.com</a> to
              extend your access to all 12 exams and your attempt history.
            </p>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2">Practice Exam Suite</p>
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                Choose an exam below to begin
              </h1>
              <p className="mt-2 text-slate-500 max-w-2xl">
                Each exam has 180 questions across People, Process, and Business Environment, timed like the
                actual PMP exam in Testing Mode. Study Mode allows you to see answers as you go.
              </p>
            </div>

            {/* Deliberately NO guarantee/readiness tracker here — admin-only (product decision) */}
            {inProgress.length > 0 && (
              <div data-testid="resume-banner" className="mb-8 rounded-xl border border-blue-200 bg-blue-50 overflow-hidden">
                {inProgress.map((a) => (
                  <div key={a.id} className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-blue-100 last:border-b-0">
                    <PlayCircle className="w-5 h-5 text-blue-700 shrink-0" />
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-sm font-medium text-slate-900">
                        Exam {a.exam_number} in progress — {MODE_META[a.mode].label}
                      </div>
                      <div className="text-xs text-slate-500">
                        {a.answered_count} of 180 answered · at question {a.current_position}
                      </div>
                    </div>
                    <button
                      data-testid={`resume-exam-${a.exam_number}-${a.mode}`}
                      onClick={() => navigate(`/exam/${a.id}`)}
                      className="bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                    >
                      Resume
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!exams ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-56 bg-white border border-slate-200 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {exams.map((ex) => (
                  <ExamCard key={ex.exam_number} ex={ex} start={start} starting={starting} navigate={navigate} />
                ))}
              </div>
            )}

            <footer className="mt-14 border-t border-slate-200 pt-6 pb-2">
              <div className="mb-3">
                <ReviewWidget />
              </div>
              <p className="text-xs text-slate-400 mb-3">
                Need help?{" "}
                <a data-testid="support-link" href="mailto:support@instructing.com" className="underline hover:text-slate-600">
                  support@instructing.com
                </a>
              </p>
              <p data-testid="pmi-attribution" className="text-[11px] text-slate-400 leading-relaxed max-w-4xl">
                Instructing.com, LLC has agreed to abide by PMI-established quality assurance criteria. Project
                Management Professional (PMP)®, Certified Associate in Project Management (CAPM)®, PMI Agile
                Certified Practitioner (PMI-ACP)®, PMI Professional in Business Analysis (PMI-PBA)®, and A Guide
                to the Project Management Body of Knowledge, (PMBOK® Guide) are registered trademarks of the
                Project Management Institute, Inc.
              </p>
            </footer>
          </>
        )}
      </main>

      {pending && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={() => setPending(null)} />
          <div data-testid="exam-instructions-modal" className="relative bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.15)] max-w-lg w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900">
              Before you begin — Exam {pending.examNumber}, {pending.mode === "testing" ? "Testing Mode" : "Study Mode"}
            </h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-600 leading-relaxed list-disc pl-5">
              <li>180 questions across People, Process, and Business Environment. Question and answer order is shuffled for every attempt.</li>
              {pending.mode === "testing" ? (
                <>
                  <li>You have <strong>230 minutes</strong>. The timer starts now and keeps running even if you exit — the exam auto-submits when time expires.</li>
                  <li>The exam runs in <strong>three sections of 60 questions</strong>, like the real PMP exam. After sections 1 and 2 you may take an optional <strong>10-minute break</strong> — your clock pauses during the break, and time beyond 10 minutes is deducted.</li>
                  <li>Ending a section is final: you review it, end it, and <strong>cannot return to its questions</strong>.</li>
                  <li>No answers or explanations are shown until you submit.</li>
                </>
              ) : (
                <>
                  <li>Untimed. Select an answer, then <strong>Check Answer</strong> to see the correct answer and explanation immediately.</li>
                  <li>A running tally of correct and incorrect answers stays in the header.</li>
                </>
              )}
              <li>Tools: highlight text in the question, strike out answer choices with the circle-slash button, and <strong>Mark for Review</strong> to flag questions. The <strong>Review Screen</strong> shows all questions at a glance.</li>
              <li>Press <strong>Enter</strong> to move to the next question after selecting an answer.</li>
              <li>You can exit anytime — progress is saved and you can resume from the dashboard.</li>
            </ul>
            <div className="mt-6 flex justify-end gap-3">
              <button data-testid="cancel-instructions-btn" onClick={() => setPending(null)}
                className="text-sm font-medium px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button data-testid="begin-exam-btn" onClick={beginExam}
                className="text-sm font-medium px-5 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors">
                Begin
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ExamCard = ({ ex, start, starting, navigate }) => {
  const n = ex.exam_number;
  return (
    <div data-testid={`exam-card-${n}`} className="group bg-white rounded-xl border border-slate-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.07)] hover:border-blue-300 transition-[box-shadow,border-color] duration-200 flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400">Exam {String(n).padStart(2, "0")}</div>
          <h3 className="text-lg font-semibold text-slate-900 mt-0.5">PMP Practice Exam {n}</h3>
        </div>
        <div className="text-right">
          {ex.best_pct != null ? (
            <>
              <div data-testid={`best-score-${n}`} className="font-mono text-xl font-semibold text-blue-700">{ex.best_pct}%</div>
              <div className="text-[11px] text-slate-400">best score</div>
              {ex.attempts[0] && (
                <div data-testid={`last-score-${n}`} className="text-[11px] text-slate-500 font-mono mt-0.5">
                  last {ex.attempts[0].score_pct}%
                </div>
              )}
            </>
          ) : (
            <span className="text-[11px] text-slate-400 bg-slate-100 px-2 py-1 rounded-full">Not attempted</span>
          )}
        </div>
      </div>
      <div className="text-xs text-slate-500 mb-4">180 questions · People / Process / Business Environment</div>

      {ex.attempts.length > 0 && (
        <div className="mb-4 space-y-1">
          {ex.attempts.slice(0, 3).map((a) => (
            <button
              key={a.id}
              data-testid={`attempt-link-${a.id}`}
              onClick={() => navigate(`/report/${a.id}`)}
              className="w-full flex items-center justify-between text-xs px-2.5 py-1.5 rounded-md bg-slate-50 hover:bg-slate-100 text-slate-600 transition-colors"
            >
              <span>{a.mode === "testing" ? "Testing" : "Study"} · {a.completed_at ? new Date(a.completed_at).toLocaleDateString() : ""}</span>
              <span className="flex items-center gap-1 font-mono font-medium text-slate-800">
                {a.score_pct}% <ChevronRight className="w-3 h-3 text-slate-400" />
              </span>
            </button>
          ))}
        </div>
      )}

      <div className="mt-auto grid grid-cols-2 gap-2">
        {["testing", "study"].map((mode) => {
          const Icon = MODE_META[mode].icon;
          const resume = ex.in_progress?.[mode];
          return (
            <button
              key={mode}
              data-testid={`start-${mode}-${n}`}
              title={MODE_META[mode].hint}
              disabled={starting === `${n}-${mode}`}
              onClick={() => (resume ? navigate(`/exam/${resume.id}`) : start(n, mode))}
              className={`flex items-center justify-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border transition-colors disabled:opacity-60 ${
                mode === "testing"
                  ? "bg-blue-700 hover:bg-blue-800 text-white border-blue-700"
                  : "bg-white hover:bg-slate-50 text-slate-700 border-slate-300"
              }`}
            >
              <Icon className="w-4 h-4" strokeWidth={1.75} />
              {resume ? "Resume" : mode === "testing" ? "Testing" : "Study"}
            </button>
          );
        })}
      </div>
    </div>
  );
};
